import { Box, Popper } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { dicomColorToRgbString } from '~/utils/color';
import { dicomColorsEqual } from '~/utils/colorUtils';
import DicomColorPicker from './DicomColorPicker';

/**
 * Color swatch + popper picker for plain {@link DicomColor} state. Use
 * this wherever a color is chosen BEFORE a structure exists (operation
 * panels, creation dialogs); `StructureColorPopover` is the control for
 * an existing structure's color — it is bound to a structure and carries
 * confirm/cancel semantics this control does not need.
 *
 * The live drag color is LOCAL state: react-colorful fires `onChange` per
 * pointermove, and forwarding every move to the host re-rendered the
 * entire operation panel (sliders, toggle groups, validated text fields)
 * per move. The parent's `onChange` fires once per gesture — on
 * pointer-up and whenever the popper closes — so callers still always
 * receive the final committed color.
 */
export default function ColorSwatchPicker({
    color,
    onChange,
    ariaLabel,
    dataTestId,
    size = 22,
}: {
    color: DicomColor;
    onChange: (color: DicomColor) => void;
    ariaLabel: string;
    dataTestId?: string;
    size?: number;
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [draftColor, setDraftColor] = useState<DicomColor>(color);
    // The commit handlers read the draft through a ref so their identity
    // does not change per pointermove (which would re-bind the Escape
    // listener mid-drag).
    const draftRef = useRef(draftColor);
    draftRef.current = draftColor;

    // Adopt external changes (e.g. the parent resetting the form). After a
    // commit this re-runs with the value the draft already holds — a no-op.
    useEffect(() => {
        setDraftColor(color);
    }, [color]);

    const commitDraft = useCallback(() => {
        const draft = draftRef.current;
        if (!dicomColorsEqual(draft, color)) {
            onChange(draft);
        }
    }, [color, onChange]);

    const closePicker = useCallback(() => {
        // Closing always commits: losing an adjustment because the popper
        // was dismissed mid-gesture would read as a dropped edit.
        commitDraft();
        setAnchorEl(null);
    }, [commitDraft]);

    // Capture-phase Escape: close only the picker, and stop the event so a
    // host's own document-level Escape handler does not also close the host
    // under it.
    useEffect(() => {
        if (!anchorEl) {
            return;
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                closePicker();
            }
        };
        document.addEventListener('keydown', handleKeyDown, true);
        return () =>
            document.removeEventListener('keydown', handleKeyDown, true);
    }, [anchorEl, closePicker]);

    // Capture-phase click-away, in place of MUI's ClickAwayListener: a host
    // of the picker can stop click propagation, and a bubble-phase document
    // listener then never hears a click inside that host, so the picker
    // stays open. The capture phase runs before any stopPropagation. The
    // swatch itself is excluded: its own onClick is the open/close toggle,
    // and a close here first would make that toggle open the picker again.
    const popperContentRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!anchorEl) {
            return;
        }
        const handleAway = (event: Event) => {
            const target = event.target;
            if (
                target instanceof Node &&
                (popperContentRef.current?.contains(target) ||
                    anchorEl.contains(target))
            ) {
                return;
            }
            closePicker();
        };
        document.addEventListener('mousedown', handleAway, true);
        document.addEventListener('touchstart', handleAway, true);
        return () => {
            document.removeEventListener('mousedown', handleAway, true);
            document.removeEventListener('touchstart', handleAway, true);
        };
    }, [anchorEl, closePicker]);

    return (
        <>
            <Box
                component="button"
                type="button"
                aria-label={ariaLabel}
                data-testid={dataTestId}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                    if (anchorEl) {
                        closePicker();
                    } else {
                        setAnchorEl(e.currentTarget);
                    }
                }}
                sx={{
                    width: size,
                    height: size,
                    borderRadius: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    p: 0,
                    backgroundColor: dicomColorToRgbString(draftColor),
                }}
            />
            <Popper
                open={!!anchorEl}
                anchorEl={anchorEl}
                placement="top-start"
                sx={{ zIndex: 1500 }}
            >
                {/* react-colorful captures the pointer, so the up event
                    bubbles through this Box even when released outside
                    the picker area. */}
                <Box
                    ref={popperContentRef}
                    sx={{ p: 1 }}
                    onPointerUp={commitDraft}
                >
                    <DicomColorPicker
                        color={draftColor}
                        onChange={setDraftColor}
                    />
                </Box>
            </Popper>
        </>
    );
}
